import { FC, InputHTMLAttributes, useContext, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Select, { GroupBase, OptionsOrGroups, SingleValue } from "react-select";
import ReactInputMask from "react-input-mask";

import HubInputField from "src/components/inputType";

import { AddPracticeSchema } from "src/validation/addPractice";
import useFetch from "src/hooks/useFetch";
import { LoaderContext } from "src/context/loaderContext";
import { ToastContext } from "src/context/toastContext";
import { populateAddPracticeForm } from "src/helpers/addPractice";
import { AuthContext } from "src/context/authContext";

import { ENROLLMENT_TIMEZONE, US_STATES } from "src/constants";
import { AddPracticeModalProps } from "./types";
import { APIEndpoints } from "src/types/apiTypes";
import { NPILookupResponse, ToastTypes } from "src/types";
import { AddPracticeFormProps } from "src/pages/addPractice/types";




const AddPracticeModal: FC<AddPracticeModalProps> = ({ title, shouldBeAbleToSearch, isModalVisible, is_provider, updateSelectedPractice, hideModal }): JSX.Element => {
  type OptionType = { value: string; label: string };

  const [preservedNpi, updatePreservedNpi] = useState<string>("");

  const { setLoader, hideLoader } = useContext(LoaderContext)
  const { showToast } = useContext(ToastContext)
  const { userData } = useContext(AuthContext)
  // const [dropdownOptions, setDropdownOptions] = useState<any>();

  const { control, formState, handleSubmit: handlePracticeSubmit, getValues, setValue, reset } =
    useForm<AddPracticeFormProps>({
      mode: "all",
      resolver: yupResolver(AddPracticeSchema)
    })

  const SOC = [{
    value:'Ambulatory Surgical Center(POS 24)',
    label:'Ambulatory Surgical Center(POS 24)',
  },{
    value:'Physicians Office(POS 11)',
    label:'Physicians Office(POS 11)',
  },{
    value:'Hospital Outpatient On-Campus(POS 22)',
    label:'Hospital Outpatient On-Campus(POS 22)',
  },{
    value:'Hospital Outpatient Off-Campus(POS 19)',
    label:'Hospital Outpatient Off-Campus(POS 19)',
  },{
    value:'Anesthesiology Group',
    label:'Anesthesiology Group',
  }]

  /**
   * @NPILookup calling npi lookup for details
   * @first useFetch hook for api call
   * @second useFetch error handler
   * @third useFetch success handler
   * @fourth npi lookup api trigger
   */
  const { data: npiLookupData, error: npiLookupError, mutate: lookupNpi, removeErrorAndData: clearNpiData } =
    useFetch<NPILookupResponse>(APIEndpoints.NPI_LOOKUP);

  /** @third */
  useEffect(() => {
    if (npiLookupData || npiLookupError) {
      hideLoader();
      if (npiLookupData) {
        if (npiLookupData.practice_data !== null) {
          showToast([`Practice Data Found`], ToastTypes.INFO);
          populateAddPracticeForm(setValue, npiLookupData.practice_data, getValues("npi"))
        }

        if (npiLookupData.practice_data === null)
          showToast([`No Data Found`], ToastTypes.ERROR);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npiLookupData, npiLookupError]);

  /** @fourth */
  const callNPILookup = () => {
    const npi = getValues("npi");

    if (!npiLookupData || npi !== preservedNpi) {
      const npiError = formState.errors && formState.errors.npi;

      if (
        !npi ||
        (npi && npi.length === 0) ||
        npiError
      )
        showToast([`Please enter valid NPI for lookup`], ToastTypes.ERROR);
      else {
        setLoader();
        clearNpiData();
        updatePreservedNpi(npi);
        const data = {
          'global_doctor[npi]': npi,
        };
        lookupNpi(null, null, data);
      }
    }
  };




  const { loading: addPracticeLoader, data: addPracticeRes, error: addPracticeErr, mutate: addNewPractice } =
    useFetch<{ practice_id: number; status: number; success: boolean; } | { message: string; practice_id: number; }>(APIEndpoints.ADD_PRACTICE)

  useEffect(() => {
    if (addPracticeRes || addPracticeErr) {
      if (addPracticeRes) {
        const { status, practice_id } = addPracticeRes as { practice_id: number; status: number; success: boolean; }

        if (status === 200) {
          showToast(["Facility added successfully"], ToastTypes.SUCCESS)
          const { name, address: { street, suite, city, state, zip } } = getValues()
          updateSelectedPractice({ value: practice_id, label: `${name} - ${street}${suite ? ` ${suite}` : ""} ${city}, ${state} ${zip}` }, true)
          reset()
          hideModal()
        } else {
          const { message } = addPracticeRes as { message: string; practice_id: number; }
          showToast([message], ToastTypes.WARNING)
          const { name, address: { street, suite, city, state, zip } } = getValues()
          updateSelectedPractice({ value: practice_id, label: `${name} - ${street}${suite ? ` ${suite}` : ""} ${city}, ${state} ${zip}` }, false)
          reset()
          hideModal()
        }
      }

      if (addPracticeErr) {
        console.log({ addPracticeErr })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addPracticeRes, addPracticeErr])

  const submitPracticeForm: SubmitHandler<AddPracticeFormProps> = async (values: AddPracticeFormProps) => {
    let practice_type;
    if (title) {
      if (is_provider) {
        practice_type = "treating_provider_as_practice"
      } else {
        if (title.includes("Referring"))
          practice_type = "referring"
        else
          practice_type = "treating"
      }
    }

    const { phone_number, soc_type, ...practiceValues } = values;
    const practice_attributes_attributes = [
      {
        name: "soc",
        value: values.soc_type
      }
    ]
    const submittingValues = {
      ...practiceValues,
      practice_type,
      ref_id: !shouldBeAbleToSearch ? (userData ? userData.ref_id : 0) : "",
      ref_type: !shouldBeAbleToSearch ? (userData ? userData.ref_type : "") : "",
      phone_number: {
        number: values.phone_number,
        phone_type: "work",
      },
      practice_attributes_attributes,
    }

    addNewPractice(submittingValues)
  }



  return (
    <div
      className={`modal fade ${isModalVisible ? 'show show-modal' : ''}`}
      id="addPracticeModal"
      tabIndex={-1}
      data-bs-backdrop="true"
      aria-hidden="true"
      onClick={hideModal}
    >
      <div className="modal-dialog modal-dialog-centered modal-xl" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h3 className="modal-title" id="addPracticeModalLabel">
              {title}
            </h3>
            <button type="button" className="btn-close text-light" aria-label="Close" onClick={hideModal}></button>
          </div>

          <div className="modal-body">
            <form>
              <Controller
                name="npi"
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    inputId="npi"
                    inputLabel="NPI"
                    isRequired={true}
                    isRowBased={true}
                    inputPlaceholder="NPI should be 10 digits"
                    field={field}
                    error={error}
                    onBlur={callNPILookup}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        callNPILookup();
                      }
                    }}
                  />
                )}
              />

              <div className="row">
                <div className="col-md-6">
                  <Controller
                    name="name"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="name"
                        inputLabel="Practice Name"
                        inputPlaceholder="Specify Practice Name"
                        isRequired={true}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                      />
                    )}
                  />
                </div>

                <div className="col-md-6">
                  <Controller
                    name="soc_type"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => {
                      const dropdownOptions: OptionType[] = SOC
                        .map((item) => {
                          return {
                            value: item.value,
                            label: item.label,
                          };
                        }) || [];

                      return (
                        <div className="row mb-2">
                         <label className="form-label col-lg-4 col-md-4 col-12" htmlFor="soc_type">Facility Type*</label>
                          <div className="col-lg-8 col-md-8 col-12">
                            <Select<OptionType, false>
                              options={dropdownOptions}
                              placeholder="Select SoC Type..."
                              isClearable
                              value={dropdownOptions.find((opt) => opt.value === field.value) || null}
                              onChange={(selectedOption: SingleValue<OptionType>) => {
                                field.onChange(selectedOption?.value || "");
                              }}
                            />
                            {error && <p style={{ color: "red" }}>{error.message}</p>}
                          </div>
                        </div>
                      );
                    }}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    name="office_contact"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="office_contact"
                        inputLabel="Office Contact"
                        inputPlaceholder="Specify Contact Name"
                        isRequired={false}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                      />
                    )}
                  />
                </div>

                <div className="col-md-6">
                  <Controller
                    name="tax_id"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="tax"
                        inputLabel="Tax ID"
                        isRequired={false}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                      />
                    )}
                  />
                </div>

                <div className="col-md-6">
                  <Controller
                    name="phone_number"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <div className="row mb-2">
                        <label htmlFor="phone_number" className="form-label col-lg-4 col-md-4 col-12">
                          Phone Number *
                        </label>
                        <div className="col-lg-8 col-md-8 col-12">
                          <ReactInputMask
                            value={field.value}
                            onBlur={field.onBlur}
                            mask="(999) 999-9999"
                            onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                          >
                            {(inputProps: InputHTMLAttributes<HTMLInputElement>) =>
                              <input {...inputProps} ref={field.ref} name={field.name} id="enrollment_practices_attributes.0.phone_number" className="form-control" placeholder="(555) 555-5555" />
                            }
                          </ReactInputMask>

                          {error && error.message ? (
                            <div dangerouslySetInnerHTML={{ __html: error.message }} className="text-danger text-small mt-2"></div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  />
                </div>

                <div className="col-md-6">
                  <Controller
                    name="fax_number"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <div className="row mb-2">
                        <label htmlFor="phone_number" className="form-label col-lg-4 col-md-4 col-12">
                          Fax Number *
                        </label>
                        <div className="col-lg-8 col-md-8 col-12">
                          <ReactInputMask
                            name={field.name}
                            value={field.value}
                            mask="(999) 999-9999"
                            onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                            onBlur={field.onBlur}
                          >
                            {(inputProps: InputHTMLAttributes<HTMLInputElement>) =>
                              <input {...inputProps} ref={field.ref} name={field.name} id="enrollment_practices_attributes.0.phone_number" type="text" className="form-control" placeholder="(555) 555-5555" />
                            }
                          </ReactInputMask>

                          {error && error.message ? (
                            <div dangerouslySetInnerHTML={{ __html: error.message }} className="text-danger text-small mt-2 "></div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  />
                </div>

                <div className="col-md-6">
                  <Controller
                    name="address.street"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="address.street"
                        inputLabel="Street"
                        inputPlaceholder="Street"
                        isRequired={true}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                      />
                    )}
                  />
                </div>

                <div className="col-lg-6">
                  <Controller
                    name="address.suite"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="address.suite"
                        inputLabel="Suite"
                        inputPlaceholder="Suite"
                        isRequired={false}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                      />
                    )}
                  />
                </div>

                <div className="col-lg-6">
                  <Controller
                    name="address.city"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputLabel="City"
                        inputId="address.city"
                        inputPlaceholder="City"
                        isRequired={true}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                      />
                    )}
                  />
                </div>

                <div className="col-lg-6">
                  <Controller
                    name="address.state"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        field={field}
                        error={error}
                        inputId="address.state"
                        inputPlaceholder="Select State..."
                        inputLabel="State"
                        isRequired={true}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        isDropdown={true}
                        isSearchable={false}
                        dropdownData={US_STATES as unknown as OptionsOrGroups<string, GroupBase<string>>}
                      />
                    )}
                  />
                </div>

                <div className="col-lg-6">
                  <Controller
                    name="address.zip"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="address.zip"
                        inputPlaceholder="Zip"
                        inputLabel="Zip"
                        isRequired={true}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                      />
                    )}
                  />
                </div>

                <div className="col-lg-6">
                  <Controller
                    name="address.timezone"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="address.timezone"
                        inputLabel="Timezone"
                        isRequired={true}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                        isDropdown={true}
                        isSearchable={false}
                        dropdownData={ENROLLMENT_TIMEZONE as unknown as OptionsOrGroups<string, GroupBase<string>>}
                      />
                    )}
                  />
                </div>
              </div>

              <button
                disabled={addPracticeLoader} type="button"
                className="btn btn-primary mt-2"
                onClick={handlePracticeSubmit(submitPracticeForm)}
              >
                {addPracticeLoader && (
                  <i className="text-white me-2 spinner-border" style={{ width: `24px`, height: `24px`, fontSize: "10px" }}></i>
                )}
                <span className="d-block d-lg-inline-block ps-2">Add Facility</span>
              </button>
            </form>
          </div>

        </div>
      </div>
    </div>
  )
}
export default AddPracticeModal;
